import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { UtilsService } from '../utils/utils.service';

@Injectable({
	providedIn: 'root',
})
export class VapiService {
	constructor(private http: HttpClient, private utils: UtilsService) {}

	saveAgentConfig(agentKey: string, fields: { enabled?: boolean; autonomy_mode?: string; paused?: boolean }, userId?: number) {
		const url = `${environment.api}ai_agents/save_agent_config`;
		const formData = this.utils.jsonToFormData({ agent_key: agentKey, user_id: userId, ...fields });
		return this.http.post(url, formData);
	}

	saveQueueAssignment(queueId: string, agentKey: string | null, agentId: number | undefined, campaignIds: number[], autonomyMode?: string, paused?: boolean, userId?: number) {
		const url = `${environment.api}ai_agents/save_queue_assignment`;
		const formData = this.utils.jsonToFormData({
			queue_id: queueId,
			agent_key: agentKey ?? '',
			agent_id: agentId,
			campaign_ids: JSON.stringify(campaignIds ?? []),
			autonomy_mode: autonomyMode,
			paused,
			user_id: userId,
		});
		return this.http.post(url, formData);
	}

	saveSchedule(agentKey: string, fields: { window_start: string; window_end: string; active_days: string[]; daily_target: number; weekly_target: number }) {
		const url = `${environment.api}ai_agents/save_schedule`;
		const formData = this.utils.jsonToFormData({
			agent_key: agentKey,
			window_start: fields.window_start,
			window_end: fields.window_end,
			active_days: JSON.stringify(fields.active_days),
			daily_target: fields.daily_target,
			weekly_target: fields.weekly_target,
		});
		return this.http.post(url, formData);
	}

	saveGlobalConfig(fields: { recording_consent?: boolean; cost_cap_daily?: number }) {
		const url = `${environment.api}ai_agents/save_global_config`;
		const formData = this.utils.jsonToFormData(fields);
		return this.http.post(url, formData);
	}

	getQueueTargets(queueId: string, campaignId?: number, limit = 100) {
		const url = `${environment.api}vapi/get_queue_targets`;
		const formData = this.utils.jsonToFormData({ queue_id: queueId, campaign_id: campaignId, limit });
		return this.http.post(url, formData);
	}

	/** Per-campaign "still has targets?" breakdown for a queue's waterfall — powers the active/exhausted tags in the assign modal. */
	getWaterfallStatus(queueId: string, campaignIds: number[]) {
		const url = `${environment.api}vapi/get_waterfall_status`;
		const formData = this.utils.jsonToFormData({ queue_id: queueId, campaign_ids: JSON.stringify(campaignIds ?? []) });
		return this.http.post(url, formData);
	}

	getActiveCalls() {
		const url = `${environment.api}vapi/get_active_calls`;
		return this.http.get(url);
	}

	/** Fresh, short-lived signed URL to play back a call's recording — request again each time, never cache it. */
	getRecordingUrl(vapiCallId: string) {
		const url = `${environment.api}vapi/${vapiCallId}/get_recording_url`;
		return this.http.get(url);
	}

	/** The AI agents catalog (ai_agents_descriptions table) — id, name, vapi_id, description, status. */
	getAgents() {
		const url = `${environment.api}vapi/get_agents`;
		return this.http.get(url);
	}

	getStats(period: 'daily' | 'weekly' | 'monthly') {
		const url = `${environment.api}vapi/${period}/get_stats`;
		return this.http.get(url);
	}

	/**
	 * Same shape as getStats() (agent_key, dials, talk_seconds, total_cost) but sourced straight
	 * from Vapi's own Analytics API in one backend call, grouped by assistant — real Vapi data for
	 * every agent at once, not limited to calls this LMS itself dispatched. No 'connected' key —
	 * Vapi's aggregate analytics has no per-assistant connect/answer breakdown.
	 */
	getVapiStats(period: 'daily' | 'weekly' | 'monthly') {
		const url = `${environment.api}vapi/${period}/get_vapi_stats`;
		return this.http.get(url);
	}
}
